'use client'

import { create } from 'zustand'

// Tipos do formulário
export type DemandType = 'pf' | 'pj' | null
export type SiteSituation = 'no_site' | 'has_site' | 'rebuild_site' | null
export type ProjectObjective = 'leads' | 'sales' | 'portfolio' | 'branding' | 'info' | null
export type ContentStatus = 'ready' | 'partial' | 'need_help' | null
export type UrgencyType = '7_days' | '15_30_days' | 'negotiate' | null
export type ProjectType = 'simples' | 'institucional' | 'personalizado' | null

// Features disponíveis
export const AVAILABLE_FEATURES = [
  { id: 'forms', label: 'Formulário de contato' },
  { id: 'whatsapp', label: 'Botão WhatsApp' },
  { id: 'gallery', label: 'Galeria de fotos' },
  { id: 'blog', label: 'Blog/Notícias' },
  { id: 'ecommerce', label: 'Loja virtual' },
  { id: 'scheduling', label: 'Agendamento online' },
  { id: 'maps', label: 'Mapa de localização' },
  { id: 'chat', label: 'Chat ao vivo' },
  { id: 'videos', label: 'Vídeos integrados' },
  { id: 'testimonials', label: 'Depoimentos' },
  { id: 'multilang', label: 'Multi-idiomas' },
  { id: 'dashboard', label: 'Painel admin' }
]

export interface FormData {
  // Dados de contato
  nome: string
  email: string
  whatsapp: string
  whatsappVerificado: boolean | null

  // Perfil
  demandType: DemandType
  empresa: string

  // Situacao
  situation: SiteSituation
  currentSiteUrl: string

  // Projeto
  objective: ProjectObjective
  features: string[]

  // Branding
  hasLogo: boolean | null
  brandColors: string[]
  referenceSites: string[]

  // Conteudo
  contentStatus: ContentStatus

  // Prazo e tipo
  urgency: UrgencyType
  projectType: ProjectType

  // Detalhes
  additional: string

  // LGPD
  lgpdConsent: boolean
}

// Steps do funil (13 passos + submitting + success)
export enum Step {
  INTRO = 0,
  NAME = 1,
  CONTACT = 2,
  PROFILE = 3,
  SITUATION = 4,
  OBJECTIVE = 5,
  FEATURES = 6,
  BRANDING = 7,
  CONTENT = 8,
  URGENCY = 9,
  DETAILS = 10,
  PROJECT_TYPE = 11,
  LGPD = 12,
  SUBMITTING = 13,
  SUCCESS = 14
}

interface AppState {
  currentStep: Step
  formData: FormData
  isSubmitting: boolean
  isComplete: boolean

  setStep: (step: Step) => void
  nextStep: () => void
  prevStep: () => void
  setFormData: (data: Partial<FormData>) => void
  setSubmitting: (value: boolean) => void
  setComplete: (value: boolean) => void
  reset: () => void
}

const initialFormData: FormData = {
  nome: '',
  email: '',
  whatsapp: '',
  whatsappVerificado: null,
  demandType: null,
  empresa: '',
  situation: null,
  currentSiteUrl: '',
  objective: null,
  features: [],
  hasLogo: null,
  brandColors: [],
  referenceSites: [],
  contentStatus: null,
  urgency: null,
  projectType: null,
  additional: '',
  lgpdConsent: false
}

const initialState = {
  currentStep: Step.INTRO,
  formData: initialFormData,
  isSubmitting: false,
  isComplete: false
}

export const useAppStore = create<AppState>((set, get) => ({
  ...initialState,

  setStep: (step) => set({ currentStep: step }),

  nextStep: () => {
    const state = get()
    const next = Math.min(state.currentStep + 1, Step.SUCCESS) as Step
    set({ currentStep: next })
  },

  prevStep: () => {
    const state = get()
    const prev = Math.max(state.currentStep - 1, Step.INTRO) as Step
    set({ currentStep: prev })
  },

  setFormData: (data) => set((state) => ({
    formData: { ...state.formData, ...data }
  })),

  setSubmitting: (value) => set({ isSubmitting: value }),
  setComplete: (value) => set({ isComplete: value }),

  reset: () => set(initialState)
}))
